import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { auth } from "./config";
import { signInWithGoogle, signOutMember, describeGoogleSignInError } from "./googleAuth";

const MemberAuthContext = createContext(null);

/**
 * Site-wide "is a visitor signed in as a member" state, backed by the rekebishaapp
 * project. Mounted once around the whole app (see App.jsx) so any page can gate
 * contact details / reviews behind it without wiring its own auth listener.
 */
export function MemberAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSigningIn, setIsSigningIn] = useState(false);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((current) => {
      setUser(current);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      isSigningIn,
      signIn: async () => {
        setIsSigningIn(true);
        try {
          await signInWithGoogle();
          return { error: null };
        } catch (error) {
          return { error: describeGoogleSignInError(error) };
        } finally {
          setIsSigningIn(false);
        }
      },
      signOut: () => signOutMember(),
    }),
    [user, loading, isSigningIn]
  );

  return <MemberAuthContext.Provider value={value}>{children}</MemberAuthContext.Provider>;
}

export const useMemberAuth = () => {
  const ctx = useContext(MemberAuthContext);
  if (!ctx) {
    throw new Error("useMemberAuth must be used within a MemberAuthProvider");
  }
  return ctx;
};
